import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { EnhancedButton } from '../../../components/ui/EnhancedButton.jsx';
import { cn } from '../../../utils/cn';

const ROICalculatorSelector = ({ onModeSelect, selectedMode = null }) => {
  const [hoveredMode, setHoveredMode] = useState(null);

  const calculatorModes = [
    {
      id: 'quick',
      title: 'Quick Calculator',
      subtitle: 'Get results in 2 minutes',
      description: 'Perfect for quick estimates and initial planning. Fill out all fields at once.',
      features: [
        'Single-page form',
        'Basic ROI calculation',
        'Industry benchmarks',
        'Instant results'
      ],
      icon: 'Zap',
      color: 'primary',
      recommended: true,
      timeEstimate: '2 minutes'
    }
    // Guided calculator temporarily disabled due to technical issues
    // {
    //   id: 'guided',
    //   title: 'Guided Calculator',
    //   subtitle: 'Comprehensive analysis',
    //   description: 'Step-by-step guidance with detailed insights and personalized recommendations.',
    //   features: [
    //     'Progressive disclosure',
    //     'Real-time feedback',
    //     'Advanced calculations',
    //     'Personalized insights',
    //     'Optional context questions'
    //   ],
    //   icon: 'Target',
    //   color: 'primary',
    //   recommended: true,
    //   timeEstimate: '4-6 minutes'
    // }
  ];

  const handleModeSelect = (mode) => {
    onModeSelect(mode);
  };

  if (selectedMode) {
    return null; // Hide selector once a mode is chosen
  }

  return (
    <div className="bg-card rounded-xl shadow-executive p-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 gradient-executive rounded-xl flex items-center justify-center mx-auto mb-4">
          <Icon name="Calculator" size={32} color="white" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          Choose Your Calculator Experience
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Select the calculator that best fits your needs. Both provide accurate ROI calculations 
          based on real market data from 200+ successful GCC implementations.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {calculatorModes.map((mode) => (
          <div
            key={mode.id}
            className={cn(
              "relative border-2 rounded-xl p-6 transition-all duration-300 cursor-pointer group",
              "hover:shadow-lg hover:scale-[1.02]",
              hoveredMode === mode.id ? "border-primary shadow-lg scale-[1.02]" : "border-border",
              mode.recommended && "ring-2 ring-primary/20"
            )}
            onMouseEnter={() => setHoveredMode(mode.id)}
            onMouseLeave={() => setHoveredMode(null)}
            onClick={() => handleModeSelect(mode.id)}
          >
            {/* Recommended Badge */}
            {mode.recommended && (
              <div className="absolute -top-3 left-6">
                <div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                  Recommended
                </div>
              </div>
            )}

            {/* Header */}
            <div className="flex items-start space-x-4 mb-4">
              <div className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300",
                hoveredMode === mode.id || mode.recommended 
                  ? mode.color === 'primary' 
                    ? "bg-primary text-primary-foreground"
                    : "bg-accent text-accent-foreground"
                  : "bg-muted text-muted-foreground"
              )}>
                <Icon name={mode.icon} size={24} />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-1">
                  <h3 className="text-xl font-bold text-foreground">
                    {mode.title}
                  </h3>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                    {mode.timeEstimate}
                  </span>
                </div>
                <p className="text-sm font-medium text-text-primary">
                  {mode.subtitle}
                </p>
              </div>
            </div>

            {/* Description */}
            <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
              {mode.description}
            </p>

            {/* Features */}
            <div className="space-y-2 mb-6">
              {mode.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon 
                    name="Check" 
                    size={14} 
                    className={cn(
                      "transition-colors duration-300",
                      hoveredMode === mode.id || mode.recommended 
                        ? "text-primary" 
                        : "text-success"
                    )} 
                  />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <EnhancedButton
              variant={mode.recommended ? "primary" : "outline"}
              size="md"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => handleModeSelect(mode.id)}
              className={cn(
                "transition-all duration-300",
                hoveredMode === mode.id && !mode.recommended && "bg-primary text-primary-foreground"
              )}
            >
              {mode.id === 'quick' ? 'Start Quick Calculator' : 'Start Guided Calculator'}
            </EnhancedButton>
          </div>
        ))}
      </div>

      {/* Additional Info */}
      <div className="mt-8 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-accent mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-1">About Our Calculations</h4>
            <p className="text-sm text-muted-foreground">
              Both calculators use the same underlying methodology based on industry benchmarks, 
              real market data, and proven cost structures from 200+ successful GCC implementations. 
              The guided version provides additional context and personalization for more accurate results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculatorSelector;
